import { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useAdminAction from "../../hooks/useAdminAction";
import { expectCode, getErrorCode } from "../../hooks/useAdminList";
import { formatDay, getToken, redirectIfUnauthorized } from "../../utils/adminUtils";
import FormField from "../../components/form/FormField";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";
import "../AdminHome/AdminHome.css";
import "./AdminSendcloudAddress.css";

const SENDCLOUD_URL = "https://account.sendcloud.com/login/";

// Mesmos tipos de usuário e endpoints da tela clássica
const USER_TYPES = [
  { key: "managers", label: "Managers", load: (service) => service.getAreaManagers(getToken()) },
  { key: "partners", label: "Partners", load: (service) => service.getBusinessPartners(getToken()) },
  { key: "sellers", label: "Sellers", load: (service) => service.getSellers(getToken()) },
];

const USER_TYPE_LABELS = { "area manager": "Manager", "business partner": "Partner" };

// Tipo de usuário no mesmo formato curto do clássico (Manager / Partner)
const userTypeLabel = (usertype) =>
  usertype ? USER_TYPE_LABELS[usertype.toLowerCase()] || usertype : null;

const joinAddress = (parts) => parts.filter(Boolean).join(", ");

const userAddress = (user) =>
  joinAddress([
    user.address,
    user.number,
    user.complement,
    user.district,
    user.city,
    user.state,
    user.postalCode,
    user.country,
  ]);

const toUserOption = (user) => ({
  value: user.username,
  name: `${user.firstname || ""} ${user.surname || ""}`.trim() || user.username,
  username: user.username,
  email: user.email,
  // A busca do Dropdown usa este campo: nome, username, e-mail e cidade
  searchLabel: [user.firstname, user.surname, user.username, user.email, user.city]
    .filter(Boolean)
    .join(" "),
  user,
});

const toAddressOption = (address) => ({
  value: address.id,
  label: joinAddress([
    `${address.street || ""} ${address.house_number || ""}`.trim(),
    address.city,
    address.country_state,
    address.country,
  ]),
  address,
});

const DetailRow = ({ label, value }) =>
  value ? (
    <div className="admin-confirm__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  ) : null;

const AdminSendcloudAddress = () => {
  const { showErrorToast } = useToastContext();
  const { run, runningId } = useAdminAction();
  const [userType, setUserType] = useState(USER_TYPES[0].key);
  const [usersByType, setUsersByType] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const handleLoadError = (e) => {
    if (redirectIfUnauthorized(e)) return;
    console.log(e);
    showErrorToast({ code: getErrorCode(e) });
  };

  // Endereços de remetente cadastrados no SendCloud
  useEffect(() => {
    let mounted = true;
    new GetUserInfoService()
      .getAdressInfo(getToken())
      .then((res) => {
        const result = expectCode(res, 200001).result;
        if (mounted) setAddresses((result?.sender_addresses || []).map(toAddressOption));
      })
      .catch(handleLoadError)
      .finally(() => mounted && setLoadingAddresses(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Usuários do tipo escolhido (carregados uma vez por tipo)
  useEffect(() => {
    if (usersByType[userType]) return undefined;
    let mounted = true;
    const type = USER_TYPES.find((item) => item.key === userType);
    setLoadingUsers(true);
    type
      .load(new AdminService())
      .then((res) => {
        const list = expectCode(res, 200001).result?.userListJSONs || [];
        // Contas canceladas não têm dados para vincular endereço
        const options = list.filter((user) => !user.cancelled).map(toUserOption);
        if (mounted) setUsersByType((prev) => ({ ...prev, [userType]: options }));
      })
      .catch(handleLoadError)
      .finally(() => mounted && setLoadingUsers(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const userOptions = useMemo(() => usersByType[userType] || [], [usersByType, userType]);
  const selectedUser = useMemo(
    () => userOptions.find((option) => option.value === selectedUsername),
    [userOptions, selectedUsername],
  );
  const selectedAddress = addresses.find((option) => option.value === selectedAddressId);

  const changeType = (key) => {
    setUserType(key);
    setSelectedUsername(null);
  };

  const link = async () => {
    const ok = await run(
      "link",
      async () =>
        expectCode(
          await new GetUserInfoService().postLinkUserToAdress(
            getToken(),
            selectedAddressId,
            selectedUsername,
          ),
          200000,
        ),
      "adminAddressLinked",
    );
    setConfirming(false);
    if (ok) setSelectedAddressId(null);
  };

  const user = selectedUser?.user;

  return (
    <div className="admin-address">
      <ConfirmActionDialog
        visible={confirming}
        title={localeText("adminAddressLinkTitle")}
        message={localeText("adminAddressConfirmMessage")}
        details={[
          { label: localeText("adminUsersUser"), value: selectedUser && `${selectedUser.name} (@${selectedUser.username})` },
          { label: localeText("adminAddressSendcloud"), value: selectedAddress?.label },
        ]}
        confirmLabel={localeText("adminAddressLink")}
        loading={runningId === "link"}
        onConfirm={link}
        onCancel={() => setConfirming(false)}
      />

      <section className="admin-address__card">
        <header className="admin-address__step">
          <span className="admin-address__step-number">1</span>
          <div>
            <h2 className="admin-address__step-title">{localeText("chooseUser")}</h2>
            <p className="admin-muted">{localeText("adminAddressUserHint")}</p>
          </div>
        </header>

        <div className="admin-form">
          <div className="admin-home-period__segmented" role="group" aria-label={localeText("userType")}>
            {USER_TYPES.map((type) => (
              <button
                key={type.key}
                type="button"
                className={classNames("admin-home-period__option", {
                  "admin-home-period__option--active": userType === type.key,
                })}
                aria-pressed={userType === type.key}
                onClick={() => changeType(type.key)}
              >
                {type.label}
              </button>
            ))}
          </div>

          <FormField label={localeText("user")} htmlFor="address-user">
            <Dropdown
              inputId="address-user"
              value={selectedUsername}
              options={userOptions}
              optionLabel="searchLabel"
              valueTemplate={(option, props) =>
                option ? `${option.name} · @${option.username}` : props.placeholder
              }
              itemTemplate={(option) => (
                <div className="admin-stack">
                  <span className="admin-strong">{option.name}</span>
                  <span className="admin-muted">
                    @{option.username}
                    {option.email ? ` · ${option.email}` : ""}
                  </span>
                </div>
              )}
              onChange={(e) => setSelectedUsername(e.value)}
              filter
              filterBy="searchLabel"
              placeholder={localeText(loadingUsers ? "adminAddressLoadingUsers" : "adminAddressSearchUser")}
              emptyFilterMessage={localeText("adminListNoResults")}
              disabled={loadingUsers}
              panelClassName="admin-multiselect-panel"
              virtualScrollerOptions={userOptions.length > 200 ? { itemSize: 52 } : undefined}
            />
          </FormField>

          {user && (
            <dl className="admin-confirm__details">
              <DetailRow label="E-mail" value={user.email} />
              <DetailRow label={localeText("adminBackofficePhoneNumber")} value={user.phonenumber} />
              <DetailRow
                label={localeText("adminBackofficeUserType")}
                value={userTypeLabel(user.usertype)}
              />
              <DetailRow
                label={localeText("adminBackofficeDateCreation")}
                value={user.dateCreation && formatDay(user.dateCreation)}
              />
              <DetailRow label={localeText("adminBackofficeOriginRegister")} value={user.originRegister} />
              <DetailRow label={localeText("adminBackofficeAddress")} value={userAddress(user)} />
            </dl>
          )}
        </div>
      </section>

      <section className={classNames("admin-address__card", { "admin-address__card--disabled": !user })}>
        <header className="admin-address__step">
          <span className="admin-address__step-number">2</span>
          <div>
            <h2 className="admin-address__step-title">{localeText("adminAddressSendcloud")}</h2>
            <p className="admin-muted">{localeText("UserAddressOnSendCloud")}</p>
          </div>
        </header>

        <div className="admin-form">
          <FormField label={localeText("address")} htmlFor="address-sendcloud">
            <Dropdown
              inputId="address-sendcloud"
              value={selectedAddressId}
              options={addresses}
              optionLabel="label"
              onChange={(e) => setSelectedAddressId(e.value)}
              filter
              placeholder={localeText(loadingAddresses ? "adminAddressLoadingAddresses" : "chooseAddress")}
              emptyFilterMessage={localeText("adminListNoResults")}
              emptyMessage={localeText("adminListEmpty")}
              disabled={!user || loadingAddresses}
              panelClassName="admin-multiselect-panel"
            />
          </FormField>

          <p className="admin-address__hint">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>
              {localeText("adminAddressNotFound")}{" "}
              <a href={SENDCLOUD_URL} target="_blank" rel="noopener noreferrer">
                {localeText("adminAddressRegisterOnSendcloud")}
                <i className="pi pi-external-link" aria-hidden="true" />
              </a>
            </span>
          </p>

          <div className="admin-address__actions">
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={() => setConfirming(true)}
              disabled={!user || !selectedAddressId || runningId === "link"}
            >
              <i className="pi pi-link" aria-hidden="true" />
              {localeText("adminAddressLink")}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminSendcloudAddress;
